import React, { useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Logo } from "./Logo";
import { HOME } from "@/constants/testIds";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const Footer = () => {
  const [email, setEmail] = useState("");
  const [status, setStatus] = useState("idle"); // idle | loading | success
  const [numberedId, setNumberedId] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    const trimmed = email.trim();
    if (!trimmed || !/^\S+@\S+\.\S+$/.test(trimmed)) {
      toast.error("Enter a valid email to enlist.");
      return;
    }
    setStatus("loading");
    try {
      const res = await axios.post(`${API}/newsletter`, { email: trimmed });
      const id = res.data?.numbered_id || "";
      setNumberedId(id);
      setStatus("success");
      if (res.data?.status === "already_enlisted") {
        toast.success(`You're already numbered. ${id}`);
      } else {
        toast.success(`Enlisted. Welcome, ${id}`);
      }
    } catch (err) {
      setStatus("idle");
      const msg =
        err?.response?.data?.detail?.[0]?.msg ||
        err?.response?.data?.detail ||
        "Something broke. Try again.";
      toast.error(typeof msg === "string" ? msg : "Enlistment failed.");
    }
  };

  return (
    <footer
      id="enlist"
      data-testid={HOME.footer}
      className="relative bg-black text-white border-t border-white/10"
    >
      <div className="max-w-[1600px] mx-auto px-6 md:px-10 py-16 md:py-20 grid grid-cols-12 gap-8">
        <div className="col-span-12 md:col-span-4">
          <Logo showWordmark={false} className="h-14 w-14 mb-6" />
          <p className="text-white/60 text-[14px] leading-[1.75] max-w-xs">
            Apparel, programs and rituals for those who train in silence. Built
            in Colorado. Numbered, never mass.
          </p>
          <div className="mt-6 font-mono text-[10px] tracking-[0.35em] text-silver-300">
            EST. XXV · N° 000247
          </div>
        </div>

        <div className="col-span-6 md:col-span-2">
          <div className="font-mono text-[10px] tracking-[0.35em] text-silver-300 mb-4">
            SHOP
          </div>
          <ul className="space-y-2 text-white/70 text-[13px]">
            <li><Link to="/#apparel" className="hover:text-white">Apparel</Link></li>
            <li><Link to="/#supplements" className="hover:text-white">Supplements</Link></li>
            <li><Link to="/app" className="hover:text-white">MBS App</Link></li>
            <li><a href="#enlist" className="hover:text-white">Notify Me</a></li>
          </ul>
        </div>

        <div className="col-span-6 md:col-span-2">
          <div className="font-mono text-[10px] tracking-[0.35em] text-silver-300 mb-4">
            TRAIN
          </div>
          <ul className="space-y-2 text-white/70 text-[13px]">
            <li><Link to="/challenge" className="text-silver-100 hover:text-white">Turn 180 →</Link></li>
            <li><Link to="/programs/ascent" className="hover:text-white">Ascent</Link></li>
            <li><Link to="/programs/altitude" className="hover:text-white">Altitude</Link></li>
            <li><Link to="/programs/stillness" className="hover:text-white">Stillness</Link></li>
            <li><Link to="/programs/iron-order" className="hover:text-white">Iron Order</Link></li>
          </ul>
        </div>

        <div className="col-span-12 md:col-span-4">
          <div className="font-mono text-[10px] tracking-[0.35em] text-silver-300 mb-4">
            DISPATCH
          </div>
          <p className="text-white/60 text-[13px] leading-[1.7] mb-4">
            One quiet letter, one Sunday a month. Get a number.
          </p>
          {status === "success" ? (
            <div className="border-t border-silver-100/60 pt-4">
              <div className="font-serif italic text-silver-100 text-lg">
                Welcome, warrior.
              </div>
              <div className="mt-1 font-mono text-[11px] tracking-[0.32em] text-white/60">
                YOUR NUMBER — {numberedId}
              </div>
            </div>
          ) : (
            <form
              className="flex items-center border-b border-white/20 pb-2 focus-within:border-silver-100 transition-colors"
              onSubmit={submit}
            >
              <input
                data-testid={HOME.newsletterInput}
                type="email"
                required
                placeholder="your@email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="flex-1 bg-transparent outline-none text-white placeholder-white/30 py-2 text-[14px]"
              />
              <button
                data-testid={HOME.newsletterSubmit}
                type="submit"
                disabled={status === "loading"}
                className="text-[10px] tracking-[0.3em] uppercase text-silver-100 hover:text-white transition-colors disabled:opacity-50"
              >
                {status === "loading" ? "Enlisting…" : "Enlist →"}
              </button>
            </form>
          )}
        </div>
      </div>

      <div className="border-t border-white/10">
        <div className="max-w-[1600px] mx-auto px-6 md:px-10 py-6 flex flex-col md:flex-row items-center justify-between gap-4 font-mono text-[10px] tracking-[0.3em] text-white/40 uppercase">
          <div>© XXV Mind. Body. Soul. — All silences reserved.</div>
          <div className="flex items-center gap-6">
            <a href="#privacy" className="hover:text-white">Privacy</a>
            <a href="#terms" className="hover:text-white">Terms</a>
            <a href="#returns" className="hover:text-white">Returns</a>
            <span className="text-silver-300">◆</span>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
